import React from 'react';
import useModal from '@/hooks/useModal';
import { ModalHeader } from '../../../modals/ModalHeader';
import PdfViewer from '@/components/common/pdf-viewer/PdfViewer';
import { actionRequiredService, GET_REVIEW_SIGN_DETAILS } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { ContentTitle } from '../ContentTitle';
import { ActionRequiredSection } from './ActionRequiredSection';
import { LoaderScreen, Typography } from '@/components/common';
import {
	ClientSignDocumentLayout,
	ClientSignDocumentLayoutHandle,
} from './ClientSignDocumentView';
import { ClientSignFieldProgressNav } from './ClientSignFieldProgressNav';
import { useClientSignOrderedFields } from './hooks/useClientSignOrderedFields';
import { ClientSignMobileView } from './ClientSignMobileView';
import { useClientSignSession } from './hooks/useClientSignSession';
import { DocumentSubmittedModalComponent } from './DocumentSubmittedModal';
import { useMediaQuery } from 'react-responsive';

interface Props {
	isOpen: boolean;
	applicationId: number | undefined;
	actionRequiredId: number | undefined;
	urlDoc?: string;
	resubmitFilesCount?: number;
	setIsModalOpen: (value: boolean) => void;
	refetch?: () => void;
	wasPreviouslyDeclined?: boolean;
}

export const ModalReviewSign: React.FC<Props> = ({
	applicationId,
	actionRequiredId,
	urlDoc,
	resubmitFilesCount,
	isOpen,
	setIsModalOpen,
	refetch,
	wasPreviouslyDeclined = false,
}) => {
	const { Modal, show, hide } = useModal();
	const {
		Modal: DocumentSubmittedModal,
		show: showDocumentSubmittedModal,
		hide: hideDocumentSubmittedModal,
	} = useModal();
	const isDesktopOrLaptop = useMediaQuery({
		query: '(min-width: 1024px)',
	});

	const [isDocumentRead, setIsDocumentRead] = React.useState(false);
	const [footer, setFooter] = React.useState<React.ReactNode>(null);

	const { data: actionDetails, isLoading } = useQuery({
		queryKey: [GET_REVIEW_SIGN_DETAILS, actionRequiredId],
		queryFn: () =>
			actionRequiredService.getActionRequireReviewSign(
				String(actionRequiredId)
			),
	});

	const isClaimUSign = Boolean(actionDetails?.data.extracted);
	const isMobileClaimUSign = isClaimUSign && !isDesktopOrLaptop;
	const desktopRef = React.useRef(isDesktopOrLaptop);
	desktopRef.current = isDesktopOrLaptop;
	const signedRef = React.useRef(false);
	const documentLayoutRef = React.useRef<ClientSignDocumentLayoutHandle>(null);
	const [mobileSubmitted, setMobileSubmitted] = React.useState(false);
	const isSignRequired = actionDetails?.data.is_signature_required;
	const documentUrl = urlDoc ?? actionDetails?.data.url_doc ?? '';
	const fields = actionDetails?.data.required_fields ?? [];
	const name = actionDetails?.data.name_doc ?? 'Document Name';
	const signDate = actionDetails?.data.signature
		? actionDetails?.data.signed_at ?? ''
		: '';

	React.useEffect(() => {
		if (isOpen) {
			show();
		}
	}, [isOpen]);

	React.useEffect(() => {
		if (!isDesktopOrLaptop) {
			setIsDocumentRead(true);
		}
	}, [isDesktopOrLaptop]);

	const handleCloseModal = (): void => {
		if (signedRef.current) refetch?.();
		hide();
		setIsModalOpen(false);
		signedRef.current = false;
		setMobileSubmitted(false);
	};

	const session = useClientSignSession({
		applicationId,
		actionRequiredId,
		extractedFields: actionDetails?.data.extracted?.form?.fields,
		requiredFields: actionDetails?.data.required_fields,
		onClose: handleCloseModal,
		onSubmitted: () => {
			signedRef.current = true;
			if (desktopRef.current) {
				hide();
				showDocumentSubmittedModal();
			} else {
				setMobileSubmitted(true);
			}
		},
		refetch,
	});

	const { orderedFields, completedFieldCount } =
		useClientSignOrderedFields(session);
	const showDesktopClaimUSignHeaderNav =
		isClaimUSign && !isMobileClaimUSign && Boolean(documentUrl) && !isLoading;
	const mobileSignatureOpen =
		isMobileClaimUSign && Boolean(session.signatureFieldId);
	const mobileSubmittedOpen = isMobileClaimUSign && mobileSubmitted;
	const mobileCoversModal = mobileSignatureOpen || mobileSubmittedOpen;

	const handleSubmittedDone = (): void => {
		hideDocumentSubmittedModal();
		refetch?.();
		handleCloseModal();
	};

	return (
		<>
			<Modal
				classNameMainContainer={
					isMobileClaimUSign ? '!p-0 items-stretch' : undefined
				}
				noMaxHeight={isMobileClaimUSign}
				classNameCardContainer={
					isMobileClaimUSign
						? `!px-0 !w-full !max-w-none h-[100dvh] max-h-[100dvh] !rounded-none relative flex flex-col overflow-hidden ${
								mobileCoversModal ? '!py-0' : '!py-4'
						  }`
						: `!bg-gradient-to-r from-white to-white md:w-full ${
								isClaimUSign ? 'max-w-[960px]' : 'max-w-[1220px]'
						  } h-[90vh] relative flex flex-col overflow-visible`
				}
				withXButton
				closeIconTopPosition={isMobileClaimUSign ? '20px' : '31px'}
				title={
					mobileCoversModal ? undefined : (
						<ModalHeader
							title={<ContentTitle title={name} time={signDate} />}
							trailing={
								showDesktopClaimUSignHeaderNav ? (
									<ClientSignFieldProgressNav
										completed={completedFieldCount}
										total={orderedFields.length}
										onPrevious={() => documentLayoutRef.current?.cycleField(-1)}
										onNext={() => documentLayoutRef.current?.cycleField(1)}
									/>
								) : undefined
							}
							classNameContent={
								isMobileClaimUSign
									? 'pl-4'
									: showDesktopClaimUSignHeaderNav
									? undefined
									: '!justify-start'
							}
							classNameImage="!block"
						/>
					)
				}
				onRequestClose={
					mobileSignatureOpen
						? () => session.setSignatureFieldId(null)
						: undefined
				}
				onClose={handleCloseModal}
				classNameContainerChildren={
					isMobileClaimUSign
						? 'flex-1 min-h-0 !overflow-hidden'
						: 'flex-1 min-h-0 xl:!max-h-[78vh] md:h-[calc(90vh-139px)] no-scrollbar-mobile !overflow-visible'
				}
				footer={footer}
				classNameFooter="shrink-0 md:pt-6"
				classNameTitle={
					isMobileClaimUSign
						? '!mb-2 min-w-0 w-full max-w-full overflow-hidden'
						: 'min-w-0 w-full max-w-full overflow-hidden'
				}
			>
				{isLoading ? (
					<LoaderScreen />
				) : (
					<section
						className={
							isMobileClaimUSign
								? 'flex flex-col h-full min-h-0'
								: 'flex flex-col md:flex-row gap-6 h-full min-h-0'
						}
					>
						<div className="flex-1 min-w-0 min-h-0">
							{isClaimUSign && documentUrl && isMobileClaimUSign && (
								<ClientSignMobileView
									documentUrl={documentUrl}
									extractedFields={actionDetails?.data.extracted?.form?.fields}
									textPages={actionDetails?.data.extracted?.text?.pages}
									documentImages={
										actionDetails?.data.extracted?.vectorImages?.images
									}
									onClose={handleCloseModal}
									session={session}
									submitted={mobileSubmitted}
									documentName={name}
									wasPreviouslyDeclined={wasPreviouslyDeclined}
								/>
							)}

							{isClaimUSign && documentUrl && !isMobileClaimUSign && (
								<ClientSignDocumentLayout
									ref={documentLayoutRef}
									documentUrl={documentUrl}
									applicationId={applicationId}
									actionRequiredId={actionRequiredId}
									extractedFields={actionDetails?.data.extracted?.form?.fields}
									requiredFields={actionDetails?.data.required_fields}
									actionDetails={actionDetails?.data}
									resubmitFilesCount={resubmitFilesCount}
									onClose={handleCloseModal}
									refetch={refetch}
									session={session}
									showInlineFieldProgressNav={false}
									wasPreviouslyDeclined={wasPreviouslyDeclined}
								/>
							)}

							{isClaimUSign && !documentUrl && (
								<div className="flex h-full items-center justify-center">
									<Typography type="regular-14" className="text-main-red">
										The document could not be loaded. Please try again.
									</Typography>
								</div>
							)}

							{!isClaimUSign && documentUrl && (
								<PdfViewer
									showZoom
									enableDragToPan
									pdfUrl={documentUrl}
									onReachEnd={() => setIsDocumentRead(true)}
								/>
							)}
						</div>

						{!isClaimUSign && (
							<div className="xl:w-[35%] shrink-0 h-full min-h-0 relative overflow-visible flex flex-col">
								<ActionRequiredSection
									actionDetails={actionDetails}
									actionRequiredId={actionRequiredId}
									isSignRequired={isSignRequired}
									applicationId={applicationId}
									isDocumentRead={isDocumentRead}
									documentUrl={documentUrl}
									setIsModalOpen={setIsModalOpen}
									refetch={refetch}
									fields={fields}
									setModalFooter={setFooter}
									resubmitFilesCount={resubmitFilesCount}
									wasPreviouslyDeclined={wasPreviouslyDeclined}
								/>
							</div>
						)}
					</section>
				)}
			</Modal>
			<DocumentSubmittedModal classNameContainerChildren="p-4 lg:w-[512px]">
				<DocumentSubmittedModalComponent
					documentName={name}
					onDone={handleSubmittedDone}
				/>
			</DocumentSubmittedModal>
		</>
	);
};
